import { restClient } from '@/Core'

import { DISK_ADMIN_API, adminPath } from './diskAdminApi'

import type { DiskAdminQuotaResult, DiskAdminQuotaWarning, DiskAdminStorage } from '../Contracts/DiskAdmin'

export interface DiskAdminQuotaTargets {
    /** Простори з диском. */
    storages: string[]
    /** Співробітники без диска: бек створить «Мій диск» з цією квотою. */
    users: string[]
}

/**
 * Квота одного чи кількох дисків; `null` — за замовчуванням (політика).
 *
 * - один простір — `PATCH /services/disk/admin/storages/{uuid}` (у журналі —
 *   звичайна зміна квоти);
 * - кілька або співробітники без диска — груповий
 *   `PATCH /services/disk/admin/storages` з `{storages, users}` (у журналі —
 *   позначка «групою»).
 *
 * Нижче зайнятого бек дозволяє, але повертає попередження з кодом
 * `below_used`: нові файли не завантажаться, доки не звільниться місце.
 */
export const UpdateDiskAdminQuotaTask = async (
    targets: DiskAdminQuotaTargets,
    quotaBytes: number | null
): Promise<DiskAdminQuotaResult> => {
    const [single] = targets.storages

    if (single !== undefined && targets.storages.length === 1 && targets.users.length === 0) {
        const response = (await restClient.patch(adminPath('storages', single), { json: { quotaBytes } })) as unknown as {
            storage?: DiskAdminStorage | null
            updated?: number
            warning?: DiskAdminQuotaWarning | null
        } | null

        return {
            items: response?.storage ? [response.storage] : [],
            updated: response?.updated ?? 0,
            warnings: response?.warning ? [response.warning] : [],
        }
    }

    const response = (await restClient.patch(`${DISK_ADMIN_API}/storages`, {
        json: { storages: targets.storages, users: targets.users, quotaBytes },
    })) as unknown as Partial<DiskAdminQuotaResult> | null

    return { items: response?.items ?? [], updated: response?.updated ?? 0, warnings: response?.warnings ?? [] }
}
